import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#syncDesktopListeners();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#syncDesktopListeners);

    this.addEventListener('click', this.navigateToProduct);

    // Lazy hydrate via IntersectionObserver — eager on every connect made whole
    // appended pages compete for bandwidth and flashed on iOS.
    this.#observeGalleryVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    mediaQueryLarge.removeEventListener('change', this.#syncDesktopListeners);
    this.#removeDesktopListeners();
    this.#stopColorTrackDrag();
    this.#galleryVisibilityObserver?.disconnect();
    this.#galleryVisibilityObserver = undefined;
  }

  /**
   * Hover preview, quick-add prefetch and the color-track wheel/drag handlers are all
   * desktop-only. Registering them on every card cost ~5 listeners x up to 96 resident
   * cards on mobile, and the `wheel` one is deliberately non-passive — which penalises
   * scrolling on touch devices purely by existing.
   */
  #syncDesktopListeners = () => {
    this.#removeDesktopListeners();
    if (!isDesktopBreakpoint()) return;

    this.addEventListener('pointerenter', this.#fetchProductPageHandler);
    this.addEventListener('focusin', this.#fetchProductPageHandler);
    this.addEventListener('wheel', this.#handleColorTrackWheel, { passive: false });
    this.addEventListener('pointerdown', this.#handleColorTrackPointerDown, { capture: true });
    this.addEventListener('dragstart', this.#handleColorTrackDragStart, { capture: true });
  };

  #removeDesktopListeners() {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);
    this.removeEventListener('wheel', this.#handleColorTrackWheel);
    this.removeEventListener('pointerdown', this.#handleColorTrackPointerDown, { capture: true });
    this.removeEventListener('dragstart', this.#handleColorTrackDragStart, { capture: true });
  }

  /**
   * Desktop: map wheel to horizontal scroll on the color thumbnail track.
   * @param {WheelEvent} event
   */
  #handleColorTrackWheel = (event) => {
    if (!isDesktopBreakpoint()) return;
    if (!(event.target instanceof Element)) return;

    const track = event.target.closest('.color-variants__track');
    if (!(track instanceof HTMLElement) || !this.contains(track)) return;
    if (track.scrollWidth <= track.clientWidth + 1) return;

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;

    const max = track.scrollWidth - track.clientWidth;
    const next = Math.min(max, Math.max(0, track.scrollLeft + delta));
    if (next === track.scrollLeft) return;

    event.preventDefault();
    this.#setColorTrackScrollLeft(track, next);
  };

  /**
   * Instant horizontal scroll — CSS `scroll-behavior: smooth` on the shared
   * track would otherwise animate every wheel/drag tick and feel laggy.
   * @param {HTMLElement} track
   * @param {number} left
   */
  #setColorTrackScrollLeft(track, left) {
    const previousBehavior = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    track.style.scrollBehavior = previousBehavior;
  }

  /** @type {{ track: HTMLElement, pointerId: number, startX: number, startScroll: number, moved: boolean, abort: AbortController } | null} */
  #colorTrackDrag = null;

  /**
   * Prevent native HTML drag of thumbnail links/images (blocks custom scroll-drag).
   * @param {DragEvent} event
   */
  #handleColorTrackDragStart = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.color-variants__track')) {
      event.preventDefault();
    }
  };

  /**
   * Desktop: click-and-drag to scroll color thumbnails.
   * @param {PointerEvent} event
   */
  #handleColorTrackPointerDown = (event) => {
    if (!isDesktopBreakpoint()) return;
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    const track = event.target.closest('.color-variants__track');
    if (!(track instanceof HTMLElement) || !this.contains(track)) return;
    if (track.scrollWidth <= track.clientWidth + 1) return;

    this.#stopColorTrackDrag();

    const abort = new AbortController();
    this.#colorTrackDrag = {
      track,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScroll: track.scrollLeft,
      moved: false,
      abort,
    };

    try {
      track.setPointerCapture(event.pointerId);
    } catch {
      // Some browsers may reject capture; window listeners still work.
    }
    track.classList.add('is-dragging');

    const { signal } = abort;
    // Listen on window so move/up still fire if the pointer leaves the track.
    window.addEventListener('pointermove', this.#handleColorTrackPointerMove, { signal });
    window.addEventListener('pointerup', this.#handleColorTrackPointerUp, { signal });
    window.addEventListener('pointercancel', this.#handleColorTrackPointerUp, { signal });
  };

  /**
   * @param {PointerEvent} event
   */
  #handleColorTrackPointerMove = (event) => {
    const drag = this.#colorTrackDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(deltaX) < 3) return;

    drag.moved = true;
    event.preventDefault();
    this.#setColorTrackScrollLeft(drag.track, drag.startScroll - deltaX);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleColorTrackPointerUp = (event) => {
    const drag = this.#colorTrackDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const { track, moved } = drag;

    if (moved) {
      track.addEventListener(
        'click',
        (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        },
        { capture: true, once: true }
      );
    }

    this.#stopColorTrackDrag();
  };

  #stopColorTrackDrag() {
    const drag = this.#colorTrackDrag;
    if (!drag) return;

    drag.abort.abort();
    drag.track.classList.remove('is-dragging');
    try {
      if (drag.track.hasPointerCapture?.(drag.pointerId)) {
        drag.track.releasePointerCapture(drag.pointerId);
      }
    } catch {
      // Pointer may already be released.
    }
    this.#colorTrackDrag = null;
  }

  /** @type {IntersectionObserver | undefined} */
  #galleryVisibilityObserver;

  /**
   * Load card images when the card is near the viewport. Keep decoded images
   * while the card stays in the DOM — page prune frees memory. Aggressive
   * dehydrate made already-seen images slow to reappear while scrolling.
   */
  #observeGalleryVisibility() {
    if (this.#galleryVisibilityObserver) return;

    this.#galleryVisibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#hydrateVisibleGallery();
          }
        }
      },
      { rootMargin: '400px 0px' }
    );
    // No synchronous getBoundingClientRect() here: on an infinite-scroll append that
    // was one forced reflow per card, interleaved with the insertion itself. The
    // observer delivers an initial entry on the next frame anyway, and the first slide
    // no longer needs hydrating — it ships as a real <img> from Liquid.
    this.#galleryVisibilityObserver.observe(this);
  }

  #hydrateVisibleGallery() {
    const slides = this.refs.slideshow?.slides;
    if (!slides?.length) return;

    // Slide 0 is server-rendered. Lazy on the rest: eager would make every hydrated
    // card fetch at once.
    this.#hydrateSlide(slides[0], false);
    this.#hydrateSlide(slides[1] ?? null, false);
    if (this.refs.slideshow?.isNested) this.#preloadNextPreviewImage();
    this.#preloadHoverImage();
  }

  #preloadNextPreviewImage() {
    const slideshow = this.refs.slideshow;
    const nextSlide = slideshow?.slides?.[slideshow.current + 1];
    this.#hydrateSlide(nextSlide, false);
    nextSlide?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Copies deferred media attrs onto a real img so the browser requests it.
   * @param {Element | null | undefined} slide
   * @param {boolean} [eager=false]
   */
  #hydrateSlide(slide, eager = false) {
    if (!(slide instanceof HTMLElement)) return;

    const holders = slide.querySelectorAll('.product-media[data-deferred-src]');
    const images = slide.querySelectorAll('img[data-src]');
    if (!holders.length && !images.length) return;

    for (const holder of holders) {
      if (!(holder instanceof HTMLElement)) continue;
      if (holder.querySelector('img.product-media__image')) continue;

      const img = document.createElement('img');
      img.className = 'product-media__image';
      img.alt = holder.getAttribute('data-alt') || '';
      img.decoding = 'async';
      img.loading = eager ? 'eager' : 'lazy';
      const sizes = holder.getAttribute('data-sizes');
      const srcset = holder.getAttribute('data-deferred-srcset');
      const src = holder.getAttribute('data-deferred-src');
      if (sizes) img.setAttribute('sizes', sizes);
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
      holder.append(img);
    }

    for (const img of images) {
      if (!(img instanceof HTMLImageElement)) continue;
      const srcset = img.getAttribute('data-srcset');
      const src = img.getAttribute('data-src');
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
      if (eager) img.loading = 'eager';
    }
  }

  /**
   * Hydrate the active slide and the next one so a swipe is not a blank frame.
   * @param {Element | null | undefined} slide
   */
  #hydrateSlideshowAround(slide) {
    const slides = this.refs.slideshow?.slides;
    this.#hydrateSlide(slide, true);
    if (!slides?.length || !(slide instanceof HTMLElement)) return;
    const index = slides.indexOf(slide);
    if (index === -1) return;
    this.#hydrateSlide(slides[index + 1] ?? null, false);
  }

  /**
   * Index of the slide shown on hover. Liquid renders the card gallery as
   * [first image, last PDP image, second image], so the hover slide is always
   * the second one (or the last available slide on 2-image products).
   * @param {ArrayLike<Element>} slides
   * @returns {number}
   */
  #hoverSlideIndex(slides) {
    return Math.min(1, slides.length - 1);
  }

  /**
   * Preloads the hover image in the slideshow.
   */
  #preloadHoverImage() {
    if (!isDesktopBreakpoint()) return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    const slides = slideshow.slides || [];
    if (slides.length > 1) {
      const hoverSlide = slides[this.#hoverSlideIndex(slides)];
      this.#hydrateSlide(hoverSlide);
      const hoverImage = hoverSlide?.querySelector('img[loading="lazy"]');
      if (hoverImage) {
        hoverImage.removeAttribute('loading');
      }
    }
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    this.#hydrateSlideshowAround(event.detail.slide);

    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the hover image (slide 2, which Liquid fills with the last PDP image).
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const slides = slideshow.slides || [];
    if (slides.length > 1) {
      const hoverSlideIndex = this.#hoverSlideIndex(slides);
      const hoverSlide = slides[hoverSlideIndex];
      this.#hydrateSlide(hoverSlide);
      const hoverImage = hoverSlide?.querySelector('img[loading="lazy"]');
      if (hoverImage) {
        hoverImage.removeAttribute('loading');
      }
      slideshow.select(hoverSlideIndex, undefined, { animate: false });
    }
  }

  /**
   * Resets the image to the first image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      // Always return to the first slide (index 0) instead of previous
      slideshow.select(0, undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
